import {
  Background,
  Controls,
  type Edge,
  Handle,
  MarkerType,
  type Node,
  type NodeProps,
  Position,
  ReactFlow,
} from '@xyflow/react'
import '@xyflow/react/dist/style.css'
import { useMemo } from 'react'

import { NODE_TYPE_LABELS, type ProcessStage, type ProcessTransition } from './api'
import { layoutProcess, STAGE_HEIGHT, STAGE_WIDTH } from './processLayout'

type StageNodeData = ProcessStage & {
  onOpen: (stage: ProcessStage) => void
  selected: boolean
  [key: string]: unknown
}

/**
 * Узел процесса.
 *
 * Точки соединения объявлены слева и справа, а не сверху и снизу: раскладка идёт
 * слева направо, и вертикальные точки заставили бы стрелки огибать карточку.
 */
function StageNode({ data }: NodeProps<Node<StageNodeData>>) {
  const classes = ['process-node', `is-${data.type}`, data.selected ? 'is-selected' : '']
    .filter(Boolean)
    .join(' ')

  // Исполнитель показывается тем, чем он задан; лейн — вычисленное подразделение, и
  // если оно и есть исполнитель, второй раз его писать незачем.
  const actor = data.actor_name ?? data.lane_department
  const laneless = data.type !== 'start' && data.type !== 'end' && data.lane_department === null

  return (
    <div className={classes} onClick={() => data.onOpen(data)} title={data.description ?? undefined}>
      <Handle type="target" position={Position.Left} />
      <div className="process-node-kind">{NODE_TYPE_LABELS[data.type]}</div>
      <div className="process-node-title">{data.title}</div>
      <div className="process-node-actor">
        {laneless ? (
          <span className="warn-inline" title="Без подразделения этап не попадёт в нить">
            подразделение не выводится
          </span>
        ) : (
          (actor ?? '—')
        )}
      </div>
      <Handle type="source" position={Position.Right} />
    </div>
  )
}

const nodeTypes = { stage: StageNode }

export default function ProcessGraph({
  stages,
  transitions,
  selectedKey,
  onOpen,
}: {
  stages: ProcessStage[]
  transitions: ProcessTransition[]
  selectedKey: string | null
  onOpen: (stage: ProcessStage) => void
}) {
  const positioned = useMemo(() => layoutProcess(stages, transitions), [stages, transitions])

  const nodes = useMemo<Node<StageNodeData>[]>(
    () =>
      positioned.nodes.map(({ stage, x, y }) => ({
        id: stage.key,
        type: 'stage',
        position: { x, y },
        draggable: false,
        data: { ...stage, onOpen, selected: stage.key === selectedKey },
        style: { width: STAGE_WIDTH, height: STAGE_HEIGHT },
        width: STAGE_WIDTH,
        height: STAGE_HEIGHT,
      })),
    [positioned.nodes, onOpen, selectedKey],
  )

  const edges = useMemo<Edge[]>(
    () =>
      transitions.map((transition) => ({
        id: String(transition.id),
        source: transition.from_node_key,
        target: transition.to_node_key,
        label: transition.label ?? undefined,
        // Кривая, а не прямая: ветки от развилки расходятся в разные ряды, и прямые
        // отрезки накладывались бы на промежуточные карточки.
        type: 'smoothstep',
        markerEnd: { type: MarkerType.ArrowClosed },
      })),
    [transitions],
  )

  if (stages.length === 0) {
    return (
      <p className="muted">
        Этапов пока нет. Процесс без этапов — законное состояние: его заводят раньше, чем
        разбирают на шаги.
      </p>
    )
  }

  return (
    <div className="process-canvas" style={{ height: Math.min(560, positioned.height + 120) }}>
      <ReactFlow
        nodes={nodes}
        edges={edges}
        nodeTypes={nodeTypes}
        fitView
        minZoom={0.2}
        proOptions={{ hideAttribution: true }}
      >
        <Background gap={20} />
        <Controls showInteractive={false} />
      </ReactFlow>
    </div>
  )
}
